import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import {
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { userPool, awsConfig } from '../config/aws';
import { getUserProfile, putUserProfile, updateUserProfile as updateProfileInDB, getUserProfileByEmail, migrateUserData } from '../utils/dynamodb';
import type { SubscriptionTier } from '../types/subscription';

export type AuthProviderType = 'password' | 'google' | 'apple' | 'tiktok' | 'facebook';

// AWS-specific UserProfile interface
export interface AWSUserProfile {
  uid: string;
  name: string;
  email: string;
  skinType?: 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal';
  acneType?: 'mild' | 'moderate' | 'severe' | 'hormonal' | 'inflammatory' | 'post-acne';
  dietaryPreference?: 'vegetarian' | 'non-vegetarian';
  gender?: 'male' | 'female' | 'prefer_not_to_say';
  subscriptionTier: SubscriptionTier;
  streak: number; // Legacy field, kept for backward compatibility
  streakCount?: number; // New field: number of consecutive days with at least one scan
  lastStreakDate?: string; // New field: date of the last day that counted toward the streak (YYYY-MM-DD in user's local timezone)
  glowScore: number;
  totalScans: number;
  createdAt: string;
  lastLoginAt: string;
  referralSource?: string;
  providers: AuthProviderType[];
  preferences: {
    notifications: boolean;
    weeklyReports: boolean;
    productRecommendations: boolean;
  };
  prescriptions?: string[]; // List of prescription medications (all plans)
  // Stripe subscription fields
  stripeCustomerId?: string;
  subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'trial_ended' | 'none';
  trialEndsAt?: string; // ISO date string
  stripeSubscriptionId?: string;
}

// Keep UserProfile for backward compatibility
export type UserProfile = AWSUserProfile;

interface AWSAuthContextType {
  user: CognitoUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, dietaryPreference?: 'vegetarian' | 'non-vegetarian', gender?: 'male' | 'female' | 'prefer_not_to_say', referralSource?: string, prescriptions?: string[]) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (referralSource?: string) => Promise<void>;
  signInWithApple: (referralSource?: string) => Promise<void>;
  signInWithTikTok: (referralSource?: string) => Promise<void>;
  handleOAuthCallback: (code: string, state?: string) => Promise<void>;
  linkProvider: (providerType: 'google' | 'apple' | 'tiktok') => Promise<void>;
  unlinkProvider: (providerType: AuthProviderType) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  getCurrentSession: () => Promise<CognitoUserSession | null>;
  cancelSubscription: () => Promise<void>;
}

const AWSAuthContext = createContext<AWSAuthContextType | undefined>(undefined);

export const useAWSAuth = () => {
  const context = useContext(AWSAuthContext);
  if (context === undefined) {
    throw new Error('useAWSAuth must be used within an AWSAuthProvider');
  }
  return context;
};

interface AWSAuthProviderProps {
  children: ReactNode;
  onError?: (message: string, type: 'error' | 'info' | 'success') => void;
}

export const AWSAuthProvider: React.FC<AWSAuthProviderProps> = ({ children, onError }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Refs for accessing current state in stable callbacks
  const userRef = useRef<CognitoUser | null>(null);
  const userProfileRef = useRef<UserProfile | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);

  // Create default user profile
  const createDefaultProfile = (
    uid: string,
    email: string,
    name: string,
    provider: AuthProviderType,
    referralSource?: string
  ): UserProfile => {
    // Check for dietary preference from sessionStorage (set during sign up)
    const dietaryPreference = sessionStorage.getItem('dietaryPreference') as 'vegetarian' | 'non-vegetarian' | null;
    if (dietaryPreference) {
      sessionStorage.removeItem('dietaryPreference'); // Clean up after use
    }

    // Check for gender from sessionStorage (set during sign up)
    const gender = sessionStorage.getItem('gender') as 'male' | 'female' | 'prefer_not_to_say' | null;
    if (gender) {
      sessionStorage.removeItem('gender'); // Clean up after use
    }

    // Check for prescriptions from sessionStorage (set during sign up)
    let prescriptions: string[] | undefined;
    try {
      const stored = sessionStorage.getItem('prescriptions');
      if (stored) {
        prescriptions = JSON.parse(stored) as string[];
        sessionStorage.removeItem('prescriptions'); // Clean up after use
      }
    } catch (_) {
      /* ignore */
    }

    return {
      uid,
      name,
      email,
      skinType: undefined,
      acneType: undefined,
      dietaryPreference: dietaryPreference || undefined,
      gender: gender || undefined,
      prescriptions: prescriptions?.length ? prescriptions : undefined,
      subscriptionTier: 'plus',
      streak: 0, // Legacy field
      streakCount: 0, // New field
      lastStreakDate: undefined, // New field
      glowScore: 0,
      totalScans: 0,
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      referralSource: referralSource || provider,
      providers: [provider],
      preferences: {
        notifications: true,
        weeklyReports: true,
        productRecommendations: true,
      },
    };
  };

  // Migrate localStorage data keyed by old uid to new uid
  const migrateLocalStorageData = (oldUid: string, newUid: string) => {
    if (oldUid === newUid) return;
    const keySuffixes = ['foodScans', 'savedDietPlans', 'savedDIYMasks'];
    for (const suffix of keySuffixes) {
      const oldKey = `${suffix}_${oldUid}`;
      const newKey = `${suffix}_${newUid}`;
      const oldData = localStorage.getItem(oldKey);
      if (oldData && !localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, oldData);
      }
    }

    // Migrate GlowBot chat history — merge sessions from old uid into new uid
    const oldChatKey = `glowbot_chat_history_${oldUid}`;
    const newChatKey = `glowbot_chat_history_${newUid}`;
    const oldChatData = localStorage.getItem(oldChatKey);
    if (oldChatData) {
      try {
        const oldSessions = JSON.parse(oldChatData) as any[];
        const existingData = localStorage.getItem(newChatKey);
        const existingSessions = existingData ? JSON.parse(existingData) as any[] : [];
        // Merge: add old sessions that don't already exist in new, then cap at 50
        const existingIds = new Set(existingSessions.map((s: any) => s.id));
        const merged = [...existingSessions];
        for (const session of oldSessions) {
          if (!existingIds.has(session.id)) {
            merged.push(session);
          }
        }
        // Sort by lastMessageAt descending and cap
        merged.sort((a: any, b: any) =>
          new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
        );
        localStorage.setItem(newChatKey, JSON.stringify(merged.slice(0, 50)));
      } catch { /* ignore malformed data */ }
    }
  };

  // Find an existing profile by email — check DynamoDB first (authoritative), then localStorage
  // Used for identity linking when the same email signs in via a different auth method
  const findExistingProfileByEmail = async (session: CognitoUserSession, email: string, excludeUid: string): Promise<UserProfile | null> => {
    // Check DynamoDB first (most up-to-date source)
    const dbProfile = await getUserProfileByEmail(session, email);
    if (dbProfile && dbProfile.uid !== excludeUid) {
      return dbProfile;
    }

    // Fallback: search localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('glowlens_profile_') && !key.endsWith(excludeUid)) {
        try {
          const profile = JSON.parse(localStorage.getItem(key)!) as UserProfile;
          if (profile.email === email) {
            return profile;
          }
        } catch { /* ignore malformed entries */ }
      }
    }

    return null;
  };

  // Subscription tier ranking for comparison
  const tierRank: Record<string, number> = { plus: 0, pro: 1, elite: 2 };

  // Migrate an existing profile to a new uid (for identity linking)
  const migrateProfileToNewUid = async (
    session: CognitoUserSession,
    oldProfile: UserProfile,
    newUid: string,
    newProvider: AuthProviderType
  ): Promise<UserProfile> => {
    const migratedProfile: UserProfile = {
      ...oldProfile,
      uid: newUid,
      lastLoginAt: new Date().toISOString(),
      providers: oldProfile.providers.includes(newProvider)
        ? oldProfile.providers
        : [...oldProfile.providers, newProvider],
    };

    // Save to localStorage under new uid
    localStorage.setItem(`glowlens_profile_${newUid}`, JSON.stringify(migratedProfile));

    // Save to DynamoDB under new uid
    putUserProfile(session, migratedProfile).catch(err =>
      console.warn('Failed to save migrated profile to DynamoDB:', err)
    );

    // Migrate scans and lifestyle data in DynamoDB
    migrateUserData(session, oldProfile.uid, newUid).catch(err =>
      console.warn('Failed to migrate user data:', err)
    );

    // Migrate localStorage data keyed by old uid
    migrateLocalStorageData(oldProfile.uid, newUid);

    return migratedProfile;
  };

  // Merge two profiles, taking the BEST value of each stat field from both
  const mergeWithRicherProfile = (current: UserProfile, other: UserProfile): UserProfile => {
    const currentRank = tierRank[current.subscriptionTier] ?? 0;
    const otherRank = tierRank[other.subscriptionTier] ?? 0;

    return {
      // Start with the profile that has the higher subscription tier
      ...(otherRank >= currentRank ? other : current),
      // Always keep current uid
      uid: current.uid,
      lastLoginAt: new Date().toISOString(),
      providers: Array.from(new Set([...current.providers, ...other.providers])),
      // Take the MAX of each numeric stat
      totalScans: Math.max(current.totalScans || 0, other.totalScans || 0),
      glowScore: Math.max(current.glowScore || 0, other.glowScore || 0),
      streak: Math.max(current.streak || 0, other.streak || 0),
      streakCount: Math.max(current.streakCount || 0, other.streakCount || 0),
      // Take the best subscription tier
      subscriptionTier: otherRank >= currentRank ? other.subscriptionTier : current.subscriptionTier,
      // Prefer non-empty values for optional fields
      skinType: current.skinType || other.skinType,
      acneType: current.acneType || other.acneType,
      dietaryPreference: current.dietaryPreference || other.dietaryPreference,
      gender: current.gender || other.gender,
      prescriptions: (current.prescriptions?.length ? current.prescriptions : other.prescriptions),
      // Use the earlier creation date
      createdAt: (current.createdAt && other.createdAt)
        ? (current.createdAt < other.createdAt ? current.createdAt : other.createdAt)
        : current.createdAt || other.createdAt,
      // Use the most recent streak date
      lastStreakDate: (current.lastStreakDate && other.lastStreakDate)
        ? (current.lastStreakDate > other.lastStreakDate ? current.lastStreakDate : other.lastStreakDate)
        : current.lastStreakDate || other.lastStreakDate,
    };
  };

  // Check if a profile by email exists under a different uid, and ALWAYS merge the best of both
  const resolveProfileByEmail = async (
    session: CognitoUserSession,
    currentProfile: UserProfile,
    email: string,
    provider: AuthProviderType
  ): Promise<UserProfile> => {
    if (!email) return currentProfile;
    const existing = await findExistingProfileByEmail(session, email, currentProfile.uid);
    if (!existing) return currentProfile;

    // Always merge — take the best stats from both profiles
    const merged = mergeWithRicherProfile(currentProfile, existing);
    // Ensure the new provider is included
    if (!merged.providers.includes(provider)) {
      merged.providers.push(provider);
    }
    localStorage.setItem(`glowlens_profile_${merged.uid}`, JSON.stringify(merged));
    // Also save to DynamoDB
    putUserProfile(session, merged).catch(err =>
      console.warn('Failed to save merged profile to DynamoDB:', err)
    );
    // Migrate scans/lifestyle from the other uid
    migrateUserData(session, existing.uid, merged.uid).catch(err =>
      console.warn('Failed to migrate user data during merge:', err)
    );
    // Migrate localStorage data from old uid
    migrateLocalStorageData(existing.uid, merged.uid);
    return merged;
  };

  // Get current session
  const getCurrentSession = useCallback((): Promise<CognitoUserSession | null> => {
    return new Promise((resolve) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        resolve(null);
        return;
      }

      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          console.error('Error getting session:', err);
          resolve(null);
        } else {
          resolve(session);
        }
      });
    });
  }, []);

  // Sign up with email and password
  const signUp = useCallback(async (email: string, password: string, name: string, dietaryPreference?: 'vegetarian' | 'non-vegetarian', gender?: 'male' | 'female' | 'prefer_not_to_say', referralSource?: string, prescriptions?: string[]): Promise<void> => {
    return new Promise((resolve, reject) => {
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'name', Value: name }),
      ];

      // Store dietary preference in sessionStorage to be saved when profile is created
      if (dietaryPreference) {
        sessionStorage.setItem('dietaryPreference', dietaryPreference);
      }

      // Store gender in sessionStorage to be saved when profile is created
      if (gender) {
        sessionStorage.setItem('gender', gender);
      }

      // Store prescriptions in sessionStorage to be saved when profile is created
      if (prescriptions && prescriptions.length > 0) {
        sessionStorage.setItem('prescriptions', JSON.stringify(prescriptions));
      }

      userPool.signUp(email, password, attributeList, [], async (err, result: ISignUpResult | undefined) => {
        if (err) {
          console.error('Sign up error:', err);
          reject(err);
          return;
        }

        if (result) {
          // User created successfully in Cognito
          // Profile will be created after email verification and first sign-in
          if (onError) {
            onError('Account created! Please check your email to verify your account.', 'success');
          }
          resolve();
        }
      });
    });
  }, [onError]);

  // Confirm sign up with verification code
  const confirmSignUp = useCallback(async (email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          console.error('Confirmation error:', err);
          reject(err);
        } else {
          if (onError) {
            onError('Email verified! You can now sign in.', 'success');
          }
          resolve();
        }
      });
    });
  }, [onError]);

  // Resend confirmation code
  const resendConfirmationCode = useCallback(async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err) => {
        if (err) {
          console.error('Resend code error:', err);
          reject(err);
        } else {
          if (onError) {
            onError('Verification code sent! Check your email.', 'info');
          }
          resolve();
        }
      });
    });
  }, [onError]);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (session) => {
          setUser(cognitoUser);
          
          // Read user info from ID token payload (no API call needed)
          const payload = session.getIdToken().payload;
          const uid = payload.sub;
          const tokenEmail = payload.email || email;
          const tokenName = payload.name || 'User';
          
          // Clear any old localStorage data from previous sessions (non-user-specific keys)
          // This ensures new users start fresh
          const oldKeys = ['savedDietPlans', 'savedDIYMasks'];
          oldKeys.forEach(key => {
            const oldData = localStorage.getItem(key);
            if (oldData) {
              localStorage.removeItem(key);
            }
          });
          
          // Try to load existing profile from localStorage
          const stored = localStorage.getItem(`glowlens_profile_${uid}`);
          let profile: UserProfile;
          
          if (stored) {
            profile = JSON.parse(stored);
            // Update last login
            profile.lastLoginAt = new Date().toISOString();
            // If profile doesn't have dietary preference but sessionStorage does, update it
            if (!profile.dietaryPreference) {
              const dietaryPreference = sessionStorage.getItem('dietaryPreference') as 'vegetarian' | 'non-vegetarian' | null;
              if (dietaryPreference) {
                profile.dietaryPreference = dietaryPreference;
                sessionStorage.removeItem('dietaryPreference');
              }
            }
            // If profile doesn't have gender but sessionStorage does, update it
            if (!profile.gender) {
              const gender = sessionStorage.getItem('gender') as 'male' | 'female' | 'prefer_not_to_say' | null;
              if (gender) {
                profile.gender = gender;
                sessionStorage.removeItem('gender');
              }
            }
            // Sync with DynamoDB — merge with the authoritative cloud copy
            const dbProfile = await getUserProfile(session, uid);
            if (dbProfile) {
              profile = mergeWithRicherProfile(profile, dbProfile);
            }
            // Check if there's a richer profile under another uid with the same email
            profile = await resolveProfileByEmail(session, profile, tokenEmail, 'password');
          } else {
            // No profile under this uid — check DynamoDB first
            const dbProfile = await getUserProfile(session, uid);
            if (dbProfile) {
              profile = { ...dbProfile, lastLoginAt: new Date().toISOString() };
              if (!profile.providers.includes('password')) {
                profile.providers.push('password');
              }
            } else {
              // Check if one exists under a different uid with the same email
              const existingProfile = tokenEmail ? await findExistingProfileByEmail(session, tokenEmail, uid) : null;
              if (existingProfile) {
                profile = await migrateProfileToNewUid(session, existingProfile, uid, 'password');
              } else {
                // Create new profile from Cognito attributes
                profile = createDefaultProfile(
                  uid,
                  tokenEmail,
                  tokenName,
                  'password'
                );
              }
            }
          }
          
          // Save to localStorage and DynamoDB
          localStorage.setItem(`glowlens_profile_${uid}`, JSON.stringify(profile));
          setUserProfile(profile);
          putUserProfile(session, profile).catch(err =>
            console.warn('Failed to sync profile to DynamoDB on sign-in:', err)
          );
          resolve();
        },
        onFailure: (err) => {
          console.error('Authentication error:', err);

          if (err && err.code === 'UserNotConfirmedException') {
            cognitoUser.resendConfirmationCode((resendErr) => {
              if (resendErr) {
                console.error('Resend confirmation code error:', resendErr);
              } else if (onError) {
                onError('Please confirm your email. We just sent you a fresh verification code.', 'info');
              }
            });
          }

          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          // Handle new password requirement
          if (onError) {
            onError('New password required. Please reset your password.', 'info');
          }
          reject(new Error('New password required'));
        },
      });
    });
  }, [onError]);

  // Sign in with Google (OAuth)
  const signInWithGoogle = useCallback(async (referralSource?: string): Promise<void> => {
    try {
      const domain = awsConfig.cognito.domain;
      const cognitoDomain = domain.includes('.amazoncognito.com') ? `https://${domain}` : `https://${domain}.auth.${awsConfig.region}.amazoncognito.com`;
      const redirectUri = awsConfig.cognito.redirectSignIn;
      const clientId = awsConfig.cognito.clientId;
      
      const oauthUrl = `${cognitoDomain}/oauth2/authorize?` +
        `identity_provider=Google&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=CODE&` +
        `client_id=${clientId}&` +
        `scope=email openid profile&` +
        // Force Google to always show the account chooser instead of silently
        // re-using the currently signed-in Google account. Cognito forwards
        // unknown query params straight through to the upstream IdP.
        `prompt=select_account`;
      
      // Store referral source for after OAuth callback
      if (referralSource) {
        sessionStorage.setItem('referralSource', referralSource);
      }
      
      // Sign out current Cognito session to force fresh Google auth
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }
      
      // Store the OAuth URL and redirect through Cognito's logout endpoint
      // to clear Cognito's session cookies — this forces Google to show the account picker
      sessionStorage.setItem('glowlens_pending_oauth_url', oauthUrl);
      const logoutUri = awsConfig.cognito.redirectSignOut;
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }, []);

  // Sign in with Apple (OAuth)
  const signInWithApple = useCallback(async (referralSource?: string): Promise<void> => {
    try {
      const domain = awsConfig.cognito.domain;
      const cognitoDomain = domain.includes('.amazoncognito.com') ? `https://${domain}` : `https://${domain}.auth.${awsConfig.region}.amazoncognito.com`;
      const redirectUri = awsConfig.cognito.redirectSignIn;
      const clientId = awsConfig.cognito.clientId;
      
      const oauthUrl = `${cognitoDomain}/oauth2/authorize?` +
        `identity_provider=SignInWithApple&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=CODE&` +
        `client_id=${clientId}&` +
        `scope=email openid profile`;
      
      if (referralSource) {
        sessionStorage.setItem('referralSource', referralSource);
      }
      
      // Sign out current Cognito session to force fresh Apple auth
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }
      
      // Store the OAuth URL and redirect through Cognito's logout endpoint
      sessionStorage.setItem('glowlens_pending_oauth_url', oauthUrl);
      const logoutUri = awsConfig.cognito.redirectSignOut;
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Apple sign-in error:', error);
      throw error;
    }
  }, []);

  // Handle OAuth callback - exchange code for tokens and sign in user
  const handleOAuthCallback = useCallback(async (code: string, state?: string): Promise<void> => {
    try {
      // Build the Cognito token endpoint URL
      const domain = awsConfig.cognito.domain;
      const tokenDomain = domain.includes('.amazoncognito.com')
        ? `https://${domain}`
        : `https://${domain}.auth.${awsConfig.region}.amazoncognito.com`;
      const tokenUrl = `${tokenDomain}/oauth2/token`;
      const redirectUri = awsConfig.cognito.redirectSignIn;
      const clientId = awsConfig.cognito.clientId;

      // Exchange authorization code for tokens
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          redirect_uri: redirectUri,
          code,
        }).toString(),
      });

      if (!response.ok) {
        const errBody = await response.text();
        console.error('Token exchange failed:', response.status, errBody);
        throw new Error('Failed to exchange authorization code for tokens');
      }

      const tokens = await response.json();
      const { id_token, access_token, refresh_token } = tokens;

      if (!id_token || !access_token) {
        throw new Error('Missing tokens in response');
      }

      // Build CognitoUserSession from the tokens
      const idToken = new CognitoIdToken({ IdToken: id_token });
      const accessToken = new CognitoAccessToken({ AccessToken: access_token });
      const refreshToken = new CognitoRefreshToken({ RefreshToken: refresh_token || '' });

      const session = new CognitoUserSession({
        IdToken: idToken,
        AccessToken: accessToken,
        RefreshToken: refreshToken,
      });

      // Extract username from the id token payload
      const payload = idToken.decodePayload();
      const username = payload['cognito:username'] || payload.sub;

      // Create CognitoUser and set the session in local storage
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });
      cognitoUser.setSignInUserSession(session);
      setUser(cognitoUser);

      // Extract profile info from the id token
      const uid = payload.sub;
      const email = payload.email || '';
      const name = payload.name || 'User';
      const identities = payload.identities;
      const provider = identities?.[0]?.providerName || 'google';
      const referralSource = sessionStorage.getItem('referralSource');

      // Load or create user profile
      const stored = localStorage.getItem(`glowlens_profile_${uid}`);
      let profile: UserProfile;

      if (stored) {
        profile = JSON.parse(stored);
        profile.lastLoginAt = new Date().toISOString();
        // Sync with DynamoDB — merge with the authoritative cloud copy
        const dbProfile = await getUserProfile(session, uid);
        if (dbProfile) {
          profile = mergeWithRicherProfile(profile, dbProfile);
        }
        // Check if there's a richer profile under another uid with the same email
        profile = await resolveProfileByEmail(session, profile, email, provider as AuthProviderType);
      } else {
        // No profile under this uid — check DynamoDB first
        const dbProfile = await getUserProfile(session, uid);
        if (dbProfile) {
          profile = { ...dbProfile, lastLoginAt: new Date().toISOString() };
          if (!profile.providers.includes(provider as AuthProviderType)) {
            profile.providers.push(provider as AuthProviderType);
          }
        } else {
          // Check if a profile exists under a different uid with the same email
          const existingProfile = email ? await findExistingProfileByEmail(session, email, uid) : null;
          if (existingProfile) {
            // Migrate the existing profile to this new uid (identity linking)
            profile = await migrateProfileToNewUid(session, existingProfile, uid, provider as AuthProviderType);
          } else {
            profile = createDefaultProfile(uid, email, name, provider as AuthProviderType, referralSource || undefined);
          }
        }
      }

      // Save profile locally and to DynamoDB
      localStorage.setItem(`glowlens_profile_${uid}`, JSON.stringify(profile));
      setUserProfile(profile);

      // Persist to DynamoDB
      putUserProfile(session, profile).catch((err) =>
        console.warn('Failed to save OAuth profile to DynamoDB:', err)
      );
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  }, []);

  // Forgot password
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          if (onError) {
            onError('Password reset code sent to your email!', 'info');
          }
          resolve();
        },
        onFailure: (err) => {
          console.error('Forgot password error:', err);
          reject(err);
        },
      });
    });
  }, [onError]);

  // Confirm password reset
  const confirmPassword = useCallback(async (email: string, code: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          if (onError) {
            onError('Password reset successfully! You can now sign in.', 'success');
          }
          resolve();
        },
        onFailure: (err) => {
          console.error('Confirm password error:', err);
          reject(err);
        },
      });
    });
  }, [onError]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    try {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  // Update user profile (stored in local state, localStorage, AND DynamoDB)
  const updateUserProfileFunc = useCallback(async (updates: Partial<UserProfile>): Promise<void> => {
    const currentUser = userRef.current;
    const currentProfile = userProfileRef.current;
    if (!currentUser || !currentProfile) return;

    try {
      // Update local profile state
      const updatedProfile = { ...currentProfile, ...updates };
      setUserProfile(updatedProfile);
      
      // Store in localStorage for persistence
      localStorage.setItem(`glowlens_profile_${currentProfile.uid}`, JSON.stringify(updatedProfile));

      // Persist to DynamoDB so changes sync across devices (web ↔ iOS)
      const session = await getCurrentSession();
      if (session) {
        updateProfileInDB(session, currentProfile.uid, updates).catch(err =>
          console.warn('Failed to sync profile update to DynamoDB:', err)
        );
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }, [getCurrentSession]);

  // Refresh user profile (from localStorage)
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    const currentUser = userRef.current;
    const currentProfile = userProfileRef.current;
    if (!currentUser || !currentProfile) return;

    try {
      const stored = localStorage.getItem(`glowlens_profile_${currentProfile.uid}`);
      if (stored) {
        setUserProfile(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, []);

  // Cancel subscription - downgrades to Plus plan
  const cancelSubscription = useCallback(async (): Promise<void> => {
    const currentUser = userRef.current;
    const currentProfile = userProfileRef.current;
    if (!currentUser || !currentProfile) {
      throw new Error('User not authenticated');
    }

    try {
      // Allow cancellation for all plans (Plus, Pro, Elite)
      // Update subscription tier to 'plus' (lowest tier)
      const updatedProfile = { ...currentProfile, subscriptionTier: 'plus' as SubscriptionTier };
      setUserProfile(updatedProfile);
      
      // Store in localStorage
      localStorage.setItem(`glowlens_profile_${currentProfile.uid}`, JSON.stringify(updatedProfile));
      
      // Update in DynamoDB
      try {
        const session = await getCurrentSession();
        if (session) {
          await updateProfileInDB(session, currentProfile.uid, { subscriptionTier: 'plus' });
        }
      } catch (dbError) {
        console.error('Error updating subscription in database:', dbError);
        // Continue even if DB update fails - local state is updated
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = userPool.getCurrentUser();
        
        if (currentUser) {
          currentUser.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session || !session.isValid()) {
              setLoading(false);
              return;
            }

            setUser(currentUser);
            
            // Read user info from ID token payload (no API call needed)
            const payload = session.getIdToken().payload;
            const uid = payload.sub;
            const email = payload.email || '';
            const name = payload.name || 'User';
            
            // Clear any old localStorage data from previous sessions (non-user-specific keys)
            const oldKeys = ['savedDietPlans', 'savedDIYMasks'];
            oldKeys.forEach(key => {
              const oldData = localStorage.getItem(key);
              if (oldData) {
                localStorage.removeItem(key);
              }
            });
            
            // Try to load profile from localStorage first
            const stored = localStorage.getItem(`glowlens_profile_${uid}`);
            let profile: UserProfile;
            
            if (stored) {
              profile = JSON.parse(stored);
              // If profile doesn't have dietary preference but sessionStorage does, update it
              if (!profile.dietaryPreference) {
                const dietaryPreference = sessionStorage.getItem('dietaryPreference') as 'vegetarian' | 'non-vegetarian' | null;
                if (dietaryPreference) {
                  profile.dietaryPreference = dietaryPreference;
                  sessionStorage.removeItem('dietaryPreference');
                }
              }
              // Sync with DynamoDB — merge with the authoritative cloud copy
              const dbProfile = await getUserProfile(session, uid);
              if (dbProfile) {
                profile = mergeWithRicherProfile(profile, dbProfile);
              }
              // Check if there's a richer profile under another uid with the same email
              profile = await resolveProfileByEmail(session, profile, email, 'password');
            } else {
              // No profile under this uid — check DynamoDB first
              const dbProfile = await getUserProfile(session, uid);
              if (dbProfile) {
                profile = { ...dbProfile, lastLoginAt: new Date().toISOString() };
              } else {
                // Check if one exists under a different uid with the same email
                const existingProfile = email ? await findExistingProfileByEmail(session, email, uid) : null;
                if (existingProfile) {
                  profile = await migrateProfileToNewUid(session, existingProfile, uid, 'password');
                } else {
                  // Create new profile from Cognito data
                  profile = createDefaultProfile(uid, email, name, 'password');
                }
              }
            }
            
            // Save synced profile to localStorage and DynamoDB
            localStorage.setItem(`glowlens_profile_${uid}`, JSON.stringify(profile));
            setUserProfile(profile);
            putUserProfile(session, profile).catch(err =>
              console.warn('Failed to sync profile to DynamoDB on checkAuth:', err)
            );
            setLoading(false);
          });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Stub implementations for methods not yet fully implemented in Cognito
  const signInWithTikTok = useCallback(async (referralSource?: string): Promise<void> => {
    throw new Error('TikTok sign-in not yet implemented for Cognito');
  }, []);

  const linkProvider = useCallback(async (providerType: 'google' | 'apple' | 'tiktok'): Promise<void> => {
    throw new Error('Provider linking not yet implemented for Cognito');
  }, []);

  const unlinkProvider = useCallback(async (providerType: AuthProviderType): Promise<void> => {
    throw new Error('Provider unlinking not yet implemented for Cognito');
  }, []);

  const value: AWSAuthContextType = useMemo(() => ({
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInWithTikTok,
    handleOAuthCallback,
    linkProvider,
    unlinkProvider,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmPassword,
    signOut,
    updateUserProfile: updateUserProfileFunc,
    refreshUserProfile,
    getCurrentSession,
    cancelSubscription,
  }), [user, userProfile, loading, getCurrentSession, handleOAuthCallback,
    signUp, signIn, signInWithGoogle, signInWithApple, signInWithTikTok,
    linkProvider, unlinkProvider, confirmSignUp, resendConfirmationCode,
    forgotPassword, confirmPassword, signOut, updateUserProfileFunc,
    refreshUserProfile, cancelSubscription]);

  return (
    <AWSAuthContext.Provider value={value}>
      {children}
    </AWSAuthContext.Provider>
  );
};

